import React, { useEffect, useState } from "react";
import { Drawer, DatePicker } from "antd";
import Select from "react-select";
import { useDispatch } from "react-redux";
import EmployeeSelect from "../../atoms/EmployeeSelect/EmployeeSelect";
import Input from "../../atoms/Input/Input";
import AddButton from "../../atoms/AddButton/AddButton";
import ErrorText from "../../atoms/Error/errorText";
import { createWfhForEmployeeAPI } from "../../../Redux/Apis/leaveApi";

const halfDaySectionOptions = [
  { value: "First Half", label: "First Half (9:00 AM - 1:00 PM)" },
  { value: "Second Half", label: "Second Half (1:00 PM - 6:00 PM)" },
];

const HrCreateWfhDrawer = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [employeeUserId, setEmployeeUserId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [wfhDayKind, setWfhDayKind] = useState("full");
  const [startDaySection, setStartDaySection] = useState(halfDaySectionOptions[0]);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  const resetForm = () => {
    setEmployeeUserId(null);
    setStartDate(null);
    setEndDate(null);
    setWfhDayKind("full");
    setStartDaySection(halfDaySectionOptions[0]);
    setReason("");
    setErrors({});
    setSubmitLoading(false);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value && endDate && endDate.isBefore(value, "day")) {
      setEndDate(value);
    }
    if (wfhDayKind === "half") {
      setEndDate(value);
    }
  };

  const handleWfhDayKindChange = (kind) => {
    setWfhDayKind(kind);
    if (kind === "half" && startDate) {
      setEndDate(startDate);
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!employeeUserId) nextErrors.employeeUserId = "Please select an employee";
    if (!startDate) nextErrors.startDate = "Please select a start date";
    if (wfhDayKind === "full" && !endDate) nextErrors.endDate = "Please select an end date";
    if (startDate && endDate && endDate.isBefore(startDate, "day")) {
      nextErrors.endDate = "End date cannot be before start date";
    }
    if (!reason.trim()) nextErrors.reason = "Please enter a reason";
    if (wfhDayKind === "half" && !startDaySection?.value) {
      nextErrors.startDaySection = "Please select which half";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (submitLoading) return;
    if (!validateForm()) return;

    setSubmitLoading(true);
    const effectiveEndDate = wfhDayKind === "half" ? startDate : endDate;
    const payload = {
      userId: employeeUserId,
      reason: reason.trim(),
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: effectiveEndDate.format("YYYY-MM-DD"),
      wfhDayKind,
    };

    if (wfhDayKind === "half" && startDaySection?.value === "First Half") {
      payload.startTime = "09:00";
      payload.endTime = "13:00";
    } else if (wfhDayKind === "half" && startDaySection?.value === "Second Half") {
      payload.startTime = "13:00";
      payload.endTime = "18:00";
    }

    try {
      await dispatch(createWfhForEmployeeAPI(payload)).unwrap();
      onClose();
    } catch (_e) {
      // Toast handled in reducer
    } finally {
      setSubmitLoading(false);
    }
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#4f46e5" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 1px #4f46e5" : "none",
      minHeight: "42px",
      borderRadius: "0.375rem",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 4000 }),
    menu: (base) => ({ ...base, zIndex: 4000 }),
  };

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      destroyOnClose
      placement={window.innerWidth > 700 ? "right" : "bottom"}
      title="Assign work from home"
      width="600px"
    >
      <div className="p-4 flex flex-col gap-4">
        <p className="m-0 text-sm text-slate-600">
          Create an approved WFH entry for an employee across one or more days. Those days show as WFH in attendance. If the employee takes approved leave (full, half, or early leave) on any day in that range, attendance shows On Leave / Early Leave for that day instead — leave takes priority over WFH.
        </p>

        {isOpen ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <EmployeeSelect
              value={employeeUserId}
              onSelectEmployee={(id) => setEmployeeUserId(id ?? null)}
              width="100%"
              placeholder="Select employee..."
              className="!mt-0"
            />
            <ErrorText show={!!errors.employeeUserId} message={errors.employeeUserId} />
          </div>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
          <DatePicker
            className={`w-full p-2 border rounded-md ${errors.startDate ? "border-red-500" : "border-gray-300"}`}
            onChange={handleStartDateChange}
            value={startDate}
            format="YYYY-MM-DD"
            placeholder="Select start date"
          />
          <ErrorText show={!!errors.startDate} message={errors.startDate} />
        </div>

        {wfhDayKind === "full" ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
            <DatePicker
              className={`w-full p-2 border rounded-md ${errors.endDate ? "border-red-500" : "border-gray-300"}`}
              onChange={(value) => setEndDate(value)}
              value={endDate}
              format="YYYY-MM-DD"
              placeholder="Select end date"
              disabledDate={(current) => startDate && current && current.isBefore(startDate, "day")}
            />
            <ErrorText show={!!errors.endDate} message={errors.endDate} />
          </div>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
          <select
            className="w-full p-2 border border-gray-300 rounded-md bg-white"
            value={wfhDayKind}
            onChange={(e) => handleWfhDayKindChange(e.target.value)}
          >
            <option value="full">Full day</option>
            <option value="half">Half day (single date only)</option>
          </select>
        </div>

        {wfhDayKind === "half" ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Which half</label>
            <Select
              value={startDaySection}
              options={halfDaySectionOptions}
              onChange={(option) => setStartDaySection(option)}
              styles={selectStyles}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
            />
            <ErrorText show={!!errors.startDaySection} message={errors.startDaySection} />
          </div>
        ) : null}

        <Input
          placeholder="Reason for working from home"
          label="Reason"
          type="text"
          name="reason"
          id="hr-wfh-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={errors.reason}
        />
        <ErrorText show={!!errors.reason} message={errors.reason} />

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700"
            onClick={onClose}
            disabled={submitLoading}
          >
            Cancel
          </button>
          <AddButton
            buttonTitle={submitLoading ? "Saving..." : "Assign WFH"}
            onClick={handleSubmit}
            className={submitLoading ? "opacity-70 pointer-events-none" : ""}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default HrCreateWfhDrawer;
